/**
 * WebAuthn / passkey helpers for SRES.
 *
 * Wraps the browser's WebAuthn API + our 4 backend endpoints under
 * /api/auth/webauthn/*. All binary fields are base64url-encoded on the wire.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/* ----------------------------- base64url utils ---------------------------- */
export function b64urlToBytes(txt) {
  let b64 = String(txt).replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad === 2) b64 += "==";
  else if (pad === 3) b64 += "=";
  else if (pad !== 0) b64 += "==";
  const raw = window.atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function bytesToB64url(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return window
    .btoa(bin)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

/* -------------------------- feature detection ---------------------------- */
export function isPasskeySupported() {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isPasskeySupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/* ------------------------------ API helpers ------------------------------ */
export async function beginRegistration() {
  const { data } = await api.post("/api/auth/webauthn/register/begin");
  return data;
}

export async function completeRegistration(credentialJSON, deviceLabel) {
  const { data } = await api.post("/api/auth/webauthn/register/complete", {
    credential: credentialJSON,
    device_label: deviceLabel,
  });
  return data;
}

export async function beginAuthentication() {
  const { data } = await api.post("/api/auth/webauthn/authenticate/begin");
  return data;
}

export async function completeAuthentication(credentialJSON, authSessionId) {
  const { data } = await api.post("/api/auth/webauthn/authenticate/complete", {
    credential: credentialJSON,
    authSessionId,
  });
  return data;
}

export async function listCredentials() {
  const { data } = await api.get("/api/auth/webauthn/credentials");
  return data.credentials || [];
}

export async function deleteCredential(id) {
  await api.delete(`/api/auth/webauthn/credentials/${id}`);
}

/* ----------------------- browser <-> wire converters --------------------- */
function optionsFromServer(pk) {
  const out = { ...pk, challenge: b64urlToBytes(pk.challenge) };
  if (pk.user) {
    out.user = { ...pk.user, id: b64urlToBytes(pk.user.id) };
  }
  if (Array.isArray(pk.excludeCredentials)) {
    out.excludeCredentials = pk.excludeCredentials.map((c) => ({
      ...c,
      id: b64urlToBytes(c.id),
    }));
  }
  if (Array.isArray(pk.allowCredentials)) {
    out.allowCredentials = pk.allowCredentials.map((c) => ({
      ...c,
      id: b64urlToBytes(c.id),
    }));
  }
  return out;
}

function attestationToJSON(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || undefined,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      attestationObject: bytesToB64url(r.attestationObject),
      transports:
        typeof r.getTransports === "function" ? r.getTransports() : undefined,
    },
    clientExtensionResults:
      typeof cred.getClientExtensionResults === "function"
        ? cred.getClientExtensionResults()
        : {},
  };
}

function assertionToJSON(cred) {
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bytesToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || undefined,
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData),
      signature: bytesToB64url(r.signature),
      userHandle: r.userHandle ? bytesToB64url(r.userHandle) : undefined,
    },
    clientExtensionResults:
      typeof cred.getClientExtensionResults === "function"
        ? cred.getClientExtensionResults()
        : {},
  };
}

/* ----------------------------- usePasskey hook --------------------------- */
export function usePasskey({ onAuthenticated } = {}) {
  const [supported, setSupported] = useState(false);
  const [platformAvailable, setPlatformAvailable] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSupported(isPasskeySupported());
    isPlatformAuthenticatorAvailable().then(setPlatformAvailable);
  }, []);

  const registerDevice = useCallback(
    async (deviceLabel = "This device") => {
      setPending(true);
      setError(null);
      try {
        if (!isPasskeySupported()) throw new Error("Passkeys are not supported in this browser");
        const { publicKey } = await beginRegistration();
        const opts = optionsFromServer(publicKey);
        const credential = await navigator.credentials.create({ publicKey: opts });
        if (!credential) throw new Error("Passkey creation was cancelled");
        return await completeRegistration(attestationToJSON(credential), deviceLabel);
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          e?.message ||
          "Passkey registration failed";
        setError(msg);
        throw new Error(msg);
      } finally {
        setPending(false);
      }
    },
    []
  );

  const signIn = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      if (!isPasskeySupported()) throw new Error("Passkeys are not supported in this browser");
      const { publicKey, authSessionId } = await beginAuthentication();
      const opts = optionsFromServer(publicKey);
      const assertion = await navigator.credentials.get({ publicKey: opts });
      if (!assertion) throw new Error("Passkey sign-in was cancelled");
      const res = await completeAuthentication(assertionToJSON(assertion), authSessionId);
      onAuthenticated?.(res);
      return res;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Passkey sign-in failed";
      setError(msg);
      throw new Error(msg);
    } finally {
      setPending(false);
    }
  }, [onAuthenticated]);

  return { supported, platformAvailable, pending, error, registerDevice, signIn };
}
